import { Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { DomesticContentAttestmentType, QueueRejectionReason, QueueStatus, QueueType } from 'types';
import { domesticContentStatusKeys } from 'types';

import { useQueueClaimConflict } from '../../hooks/useQueueClaimConflict';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueueByAccountIdAndTypeQuery, useUpdateQueueMutation } from '../../services/queues';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { DomesticContentReviewModal } from '../DomesticContentReviewModal/DomesticContentReview';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';

type DomesticContentReviewProps = {
  attestmentType?: DomesticContentAttestmentType;
};

const domesticContentQueueStatus = {
  [domesticContentStatusKeys.approvedSolarOnly]: QueueStatus.approved,
  [domesticContentStatusKeys.approvedStorageOnly]: QueueStatus.approved,
  [domesticContentStatusKeys.approvedSolarAndStorage]: QueueStatus.approved,
  [domesticContentStatusKeys.rejectedSolarAndStorage]: QueueStatus.rejected,
  [domesticContentStatusKeys.rejectedSolarOnly]: QueueStatus.rejected,
  [domesticContentStatusKeys.rejectedStorageOnly]: QueueStatus.rejected,
  [domesticContentStatusKeys.optOut]: QueueStatus.rejected,
};

export const DomesticContentReview = ({ attestmentType }: DomesticContentReviewProps) => {
  const { claims, user } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { id = '', action = 'review' } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: domesticContentQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.domesticContent,
  });
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(domesticContentQueue);

  const handleQueueUpdate = async (status: QueueStatus, note?: string, reasons?: QueueRejectionReason[]) => {
    try {
      if (domesticContentQueue?.id) {
        await updateQueue({
          id: domesticContentQueue.id,
          data: {
            assigneeId: user?.sub,
            status,
            ...(reasons && {
              rejectionReasons: [
                {
                  reasons,
                  ...(note && { note }),
                },
              ],
            }),
          },
        }).unwrap();
        toast.success('Queue updated successfully');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating queue: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating queue');
      }
    }
  };

  const handleDomesticContentModal = async (values: {
    domesticContentStatus: string;
    notes: string | undefined;
  }) => {
    const { domesticContentStatus, notes } = values;
    const status = domesticContentQueueStatus[domesticContentStatus as keyof typeof domesticContentQueueStatus];
    if (!status) {
      toast.error('Invalid status');
      return;
    }
    await handleQueueUpdate(status, notes, [domesticContentStatus as any]);
    closeModal();
  };

  return domesticContentQueue && (domesticContentQueue.status === QueueStatus.rejected || isFinCoUser) ? (
    <>
      {claimConflictModal}
      <DomesticContentReviewModal
        isModalOpen={isModalOpen}
        handleClose={closeModal}
        handleSubmit={handleDomesticContentModal}
        attestmentType={attestmentType}
      />
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.domesticContent} />
      <Box padding="md" margin="0 0 lg 0" borderColor="separator" borderWidth="xs" gap="sm">
        <h3>Domestic Content Review</h3>
        <QueueBanner
          setIsNotesOpen={setIsNotesOpen}
          queueType={QueueType.domesticContent}
          isFinCoUser={isFinCoUser}
          hideFlagCount
        />
        {action === ReviewPackageActions.review && domesticContentQueue.status !== QueueStatus.approved && (
          <Box direction="row" childGap="xs" justifyContent="flex-end" alignItems="flex-end">
            <Button
              size="sm"
              variant="primary"
              isLoading={isUpdatingQueue}
              onClick={() => checkClaimAndProceed(openModal)}
            >
              Review DC
            </Button>
          </Box>
        )}
      </Box>
    </>
  ) : null;
};
